/**
 * Structured Logger
 *
 * Provides consistent, structured logging with:
 * - Automatic trace ID inclusion
 * - Log levels (debug, info, warn, error)
 * - Structured context data
 * - Environment-aware output (JSON in production, readable in dev)
 * - Sentry integration for errors
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *
 *   logger.info('User signed in', { userId, clientId });
 *   logger.error('Failed to save', { error, donorId });
 *   logger.warn('Rate limit approaching', { remaining: 5 });
 *
 *   // Scoped logger for a feature/module:
 *   const log = logger.child('CampaignBuilder');
 *   log.info('Campaign created', { campaignId });
 */

import * as Sentry from "@sentry/react";

import { isNexusError, type NexusError } from "./errors";
import { traceContext } from "./traceContext";

// ============================================================================
// Types
// ============================================================================

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  traceId: string;
  spanId?: string;
  sessionId: string;
  module?: string;
  context?: Record<string, unknown>;
  error?: {
    name: string;
    message: string;
    category?: string;
    code?: string;
    stack?: string;
  };
}

export interface LoggerOptions {
  /** Module/feature name for scoping */
  module?: string;
  /** Minimum log level to output */
  minLevel?: LogLevel;
  /** Whether to send errors to Sentry */
  sentryEnabled?: boolean;
}

// ============================================================================
// Configuration
// ============================================================================

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Vite injects import.meta.env; Node/tsx CLI scripts get undefined there. Guard
// the access so scripts importing the logger chain don't crash (mirrors the
// Node-safe pattern already used in supabaseClient.ts).
const loggerMode = (import.meta as { env?: { MODE?: string } }).env?.MODE;
const isDev = loggerMode === "development";
const isTest = loggerMode === "test";

// Default minimum level: debug in dev, info in prod
const defaultMinLevel: LogLevel = isDev ? "debug" : "info";

// ============================================================================
// Formatters
// ============================================================================

function formatTimestamp(): string {
  return new Date().toISOString();
}

function formatForConsole(entry: LogEntry): string {
  const parts = [
    `[${entry.level.toUpperCase()}]`,
    entry.module ? `[${entry.module}]` : "",
    entry.message,
  ].filter(Boolean);

  return parts.join(" ");
}

function formatContextForConsole(
  context?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!context || Object.keys(context).length === 0) return undefined;

  // Expand any Error values so they don't collapse to `{}` when the entry is
  // JSON.stringified for prod log aggregation (Error message/stack are
  // non-enumerable). Without this, `logger.warn(msg, { error })` logs
  // `"error":{}` and hides the real cause — which masked the getSession
  // root cause for weeks (registry: supabase_get_session_root_cause_diagnosis).
  const expanded: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(context)) {
    expanded[key] =
      value instanceof Error
        ? { name: value.name, message: value.message, stack: value.stack }
        : value;
  }

  // In dev, show full context (errors already expanded above)
  if (isDev) return expanded;

  // In prod, sanitize sensitive fields
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(expanded)) {
    const lowerKey = key.toLowerCase();
    if (
      lowerKey.includes("password") ||
      lowerKey.includes("token") ||
      lowerKey.includes("secret") ||
      lowerKey.includes("key") ||
      lowerKey.includes("credential")
    ) {
      sanitized[key] = "[REDACTED]";
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

// ============================================================================
// Logger Class
// ============================================================================

class Logger {
  private module?: string;
  private minLevel: LogLevel;
  private sentryEnabled: boolean;

  constructor(options: LoggerOptions = {}) {
    this.module = options.module;
    this.minLevel = options.minLevel ?? defaultMinLevel;
    this.sentryEnabled = options.sentryEnabled ?? (!isDev && !isTest);
  }

  /**
   * Create a child logger with a module scope
   */
  child(module: string): Logger {
    return new Logger({
      module: this.module ? `${this.module}.${module}` : module,
      minLevel: this.minLevel,
      sentryEnabled: this.sentryEnabled,
    });
  }

  /**
   * Log a debug message (development only by default)
   */
  debug(message: string, context?: Record<string, unknown>): void {
    this.log("debug", message, context);
  }

  /**
   * Log an info message
   */
  info(message: string, context?: Record<string, unknown>): void {
    this.log("info", message, context);
  }

  /**
   * Log a warning message
   */
  warn(message: string, context?: Record<string, unknown>): void {
    this.log("warn", message, context);
  }

  /**
   * Log an error message
   */
  error(
    message: string,
    errorOrContext?: unknown,
    context?: Record<string, unknown>,
  ): void {
    let errorInfo: LogEntry["error"];
    let mergedContext = context ?? {};

    // Handle error object
    if (errorOrContext instanceof Error || isNexusError(errorOrContext)) {
      const err = errorOrContext as Error | NexusError;
      errorInfo = {
        name: err.name,
        message: err.message,
        stack: err.stack,
      };

      if (isNexusError(err)) {
        errorInfo.category = err.category;
        errorInfo.code = err.code;
        mergedContext = { ...err.context, ...mergedContext };
      }

      // Report to Sentry
      if (this.sentryEnabled) {
        this.reportToSentry(err, message, mergedContext);
      }
    } else if (errorOrContext && typeof errorOrContext === "object") {
      // It's context, not an error
      mergedContext = {
        ...(errorOrContext as Record<string, unknown>),
        ...mergedContext,
      };
    }

    this.log("error", message, mergedContext, errorInfo);
  }

  /**
   * Core logging method
   */
  private log(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
    error?: LogEntry["error"],
  ): void {
    // Check log level
    if (LOG_LEVELS[level] < LOG_LEVELS[this.minLevel]) {
      return;
    }

    const entry: LogEntry = {
      level,
      message,
      timestamp: formatTimestamp(),
      traceId: traceContext.getTraceId(),
      spanId: traceContext.getSpanId(),
      sessionId: traceContext.getSessionId(),
      module: this.module,
      context: formatContextForConsole(context),
      error,
    };

    // Output to console
    this.output(entry);

    // Add breadcrumb for Sentry
    if (this.sentryEnabled && level !== "debug") {
      Sentry.addBreadcrumb({
        category: this.module ?? "app",
        message,
        level: level === "warn" ? "warning" : level,
        data: {
          traceId: entry.traceId,
          ...entry.context,
        },
      });
    }
  }

  /**
   * Output log entry to console
   */
  private output(entry: LogEntry): void {
    const consoleMethod = entry.level === "debug" ? "log" : entry.level;
    const formatted = formatForConsole(entry);

    if (isDev) {
      // Readable format in development
      const args: unknown[] = [formatted];

      if (entry.context && Object.keys(entry.context).length > 0) {
        args.push(entry.context);
      }

      if (entry.error) {
        args.push(entry.error);
      }

      // Add trace ID as a subtle suffix
      args.push(`[${entry.traceId}]`);

      console[consoleMethod](...args);
    } else {
      // JSON format in production (for log aggregation)
      console[consoleMethod](JSON.stringify(entry));
    }
  }

  /**
   * Report error to Sentry
   */
  private reportToSentry(
    error: Error | NexusError,
    message: string,
    context: Record<string, unknown>,
  ): void {
    const tags: Record<string, string> = {
      traceId: traceContext.getTraceId(),
    };

    if (this.module) {
      tags.module = this.module;
    }

    if (isNexusError(error)) {
      tags.errorCategory = error.category;
      tags.errorCode = error.code;
    }

    Sentry.captureException(error, {
      level: "error",
      tags,
      contexts: {
        customContext: {
          message,
          ...context,
        },
      },
    });
  }
}

// ============================================================================
// Default Instance
// ============================================================================

export const logger = new Logger();

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Create a scoped logger for a feature/module
 */
export function createLogger(
  module: string,
  options?: Omit<LoggerOptions, "module">,
): Logger {
  return new Logger({ ...options, module });
}

/**
 * Log a timed operation
 */
export async function logTimed<T>(
  operation: string,
  fn: () => Promise<T>,
  context?: Record<string, unknown>,
): Promise<T> {
  const startTime = performance.now();
  const log = logger.child("perf");

  log.debug(`Starting: ${operation}`, context);

  try {
    const result = await fn();
    const duration = Math.round(performance.now() - startTime);

    log.info(`Completed: ${operation}`, {
      ...context,
      durationMs: duration,
    });

    return result;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);

    log.error(`Failed: ${operation}`, error, {
      ...context,
      durationMs: duration,
    });

    throw error;
  }
}

/**
 * Log and re-throw an error with additional context
 */
export function logError<E extends Error>(
  error: E,
  message: string,
  context?: Record<string, unknown>,
): E {
  logger.error(message, error, context);
  return error;
}
